import React from 'react';
import { Download, X, FileSpreadsheet } from 'lucide-react';
import { cn } from '../../utils';
import { formatFileSize, downloadFile } from '../../utils/fileUtils';
import type { FileAttachment as FileAttachmentType } from '../../types/chat';
import './FileAttachment.css';

interface FileAttachmentProps {
  attachment: FileAttachmentType;
  onRemove?: (attachmentId: string | number) => void;
  onView?: (attachment: FileAttachmentType) => void;
  showRemoveButton?: boolean;
  className?: string;
  compactMode?: boolean;
}

export const FileAttachment: React.FC<FileAttachmentProps> = ({
  attachment,
  onRemove,
  onView,
  showRemoveButton = false,
  className = '',
  compactMode = false,
}) => {
  const handleDownload = () => {
    downloadFile(attachment);
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(attachment.id);
    }
  };

  const handleCompactClick = () => {
    if (onView) {
      onView(attachment);
    } else if (attachment.url) {
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(attachment.url)}`;
      window.open(viewerUrl, '_blank', 'noopener,noreferrer');
    } else {
      handleDownload();
    }
  };

  const getFileIcon = () => {
    return <FileSpreadsheet size={20} />;
  };

  return (
    <div className={cn(
      'file-attachment',
      'file-attachment--document',
      className
    )}>
      {compactMode ? (
        // Simple compact mode for messages
        <div className="file-attachment__compact">
          <div 
            className="file-attachment__compact-content file-attachment__compact-content--clickable"
            onClick={handleCompactClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCompactClick();
              }
            }}
            title="Нажмите для просмотра"
          >
            <div className="file-attachment__icon">
              {getFileIcon()}
            </div>
            <div className="file-attachment__name" title={attachment.name}>
              {attachment.name}
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className="file-attachment__compact-download"
            title="Скачать файл"
            aria-label="Скачать файл"
          >
            <Download size={14} />
          </button>
        </div>
      ) : (
        // Full mode for file list
        <div className={cn(
          'file-attachment__preview-container',
          'file-attachment__preview-container--document',
          compactMode && 'file-attachment__preview-container--compact'
        )}>
          <div className="file-attachment__content">
            <div className="file-attachment__icon">
              {getFileIcon()}
            </div>
            
            <div className="file-attachment__info">
              <div className="file-attachment__name" title={attachment.name}>
                {attachment.name}
              </div>
              <div className="file-attachment__size">
                {formatFileSize(attachment.size)}
              </div>
            </div>

            <div className="file-attachment__actions">
              <button
                onClick={handleDownload}
                className="file-attachment__action-button"
                title="Скачать файл"
              >
                <Download size={16} />
              </button>
              
              {showRemoveButton && onRemove && (
                <button
                  onClick={handleRemove}
                  className="file-attachment__action-button file-attachment__action-button--remove"
                  title="Удалить файл"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileAttachment;
